import { queryOptions } from '@tanstack/react-query';

import { MarketEntity } from '@/entities';

import type { Exchange, NormalizedMarketItem } from '@/entities/market/types/exchange';
import type { MarketType, MarketCandleRange } from '@shared';

export const options = {
  rootKey: 'market',
  getMarkets: () =>
    queryOptions({
      queryKey: [options.rootKey],
      queryFn: () => MarketEntity.marketService.getMarkets(),
    }),
  selectFilteredMarkets: (type: MarketType) =>
    queryOptions({
      ...options.getMarkets(),
      // 🔴 `^${type}*` 오타였다 — 정규식에서 `*` 는 **바로 앞 문자**의 0회 이상 반복이라
      // `^BTC*` 는 "BT" + "C" 0개 이상, 즉 `BT-...` 같은 코드까지 통과시킨다(의도: `BTC` 로 시작).
      // 현행 업비트 접두사(KRW/BTC/USDT)에는 충돌이 없어 잠복해 있었을 뿐이다.
      // 아래 거래소별 버전(`selectFilteredMarketsByExchange`)은 처음부터 `^${marketType}` 이라
      // 두 경로가 서로 다른 규칙을 쓰고 있었다 — 같은 규칙으로 맞춘다.
      select: (data: Array<{ market: string; korean_name: string; english_name: string }>) => {
        return data.filter(item => new RegExp(`^${type}`).test(item.market));
      },
    }),
  getMarketCandlesWithUnit: (props: { market: string; range: MarketCandleRange }) =>
    queryOptions({
      queryKey: [options.rootKey, props.market, props.range],
      queryFn: () => MarketEntity.marketService.getMarketCandlesWithUnit(props),
      gcTime: 0,
    }),
  getMarketTicker: (market: string) =>
    queryOptions({
      queryKey: [options.rootKey, market],
      queryFn: () => MarketEntity.marketService.getMarketTicker(market),
    }),
  getMarketTickers: (markets: string[]) =>
    queryOptions({
      queryKey: [options.rootKey, 'tickers', markets.join(',')],
      queryFn: () => MarketEntity.marketService.getMarketTickers(markets),
      enabled: markets.length > 0,
      retry: 1,
      retryDelay: 1000,
    }),

  // --- 멀티 거래소 ---
  getMarketsByExchange: (exchange: Exchange) =>
    queryOptions({
      queryKey: [options.rootKey, 'exchange', exchange],
      queryFn: () => MarketEntity.marketService.getMarketsByExchange(exchange),
    }),
  selectFilteredMarketsByExchange: (exchange: Exchange, marketType: MarketType) =>
    queryOptions({
      ...options.getMarketsByExchange(exchange),
      select: (data: NormalizedMarketItem[]) =>
        data.filter(item => new RegExp(`^${marketType}`).test(item.market)),
    }),
  getMarketTickersByExchange: (exchange: Exchange, markets: string[]) =>
    queryOptions({
      queryKey: [options.rootKey, 'exchange', exchange, 'tickers', markets.join(',')],
      queryFn: () => MarketEntity.marketService.getMarketTickersByExchange(exchange, markets),
      enabled: markets.length > 0,
      retry: 1,
      retryDelay: 1000,
    }),

  // --- Top Movers ---
  getTopMovers: () =>
    queryOptions({
      queryKey: [options.rootKey, 'top-movers'],
      queryFn: () => MarketEntity.marketService.getTopMovers(),
      staleTime: 30_000,
    }),

  // --- 도미넌스 ---
  getDominance: () =>
    queryOptions({
      queryKey: [options.rootKey, 'dominance'],
      queryFn: () => MarketEntity.marketService.getDominance(),
      staleTime: 1000 * 60 * 5,
      refetchInterval: 1000 * 60,
    }),
};
